import { FC, useMemo, useState } from 'react';
import {
  Button,
  FeatureFlags,
  Search,
  TreeNode,
  TreeView,
} from '@carbon/react';
import { ListChecked } from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import {
  WELL_STATUS_LABELS,
  type StagedWell,
  type WellStatusToken,
} from 'types/well-workspace';
import { StatusDot } from 'components/well-cards';
import { EditedPill } from '../edited-pill';
import { wellDisplayName } from '../utils';
import { filterWells, groupWells } from './well-tree-utils';
import styles from './well-tree.module.scss';

interface Props {
  wells: StagedWell[];
  selectedUwi: string | null;
  onSelect: (uwi: string) => void;
  /**
   * Opens the Add-or-remove-wells modal. Omitted while there is no membership
   * endpoint — the control then shows, disabled, the way the bar's Comments button
   * does (see workspace-header).
   */
  onManageWells?: () => void;
  /**
   * Why the control is offered but unavailable, as its tooltip. Pass neither this nor
   * `onManageWells` for a promoted workspace, whose working set is frozen — the
   * control is then absent rather than disabled.
   */
  manageWellsHint?: string;
  /**
   * What colour a rule says a well's row should carry, or null for none — the
   * "Wells (map + list)" target. The list is half of what that target names, so the
   * rule shows here as well as on the map.
   */
  colorFor?: (well: StagedWell) => string | null;
}

// Epic 1 dropped calculated_status from the contract, so the stored status is it.
// Matches what the Well Status card reads, so the two can't disagree.
const statusOf = (well: StagedWell): WellStatusToken | null =>
  well.status as WellStatusToken | null;

const statusLabel = (well: StagedWell): string => {
  const token = statusOf(well);
  return token ? (WELL_STATUS_LABELS[token] ?? token) : '—';
};

/**
 * The workspace's well-selection tree: State -> County -> Well, with a text search over
 * well name, UWI, state and county. Selecting a leaf scopes the whole page to that well.
 *
 * Built on Carbon's TreeView, which is what the mock uses — it brings the indentation,
 * the selected highlight, and full keyboard traversal, so none of that is hand-rolled.
 * @param {Props} props The workspace's wells, the scoped UWI, and the select handler.
 * @return {JSX.Element} The search box and tree.
 */
export const WellTree: FC<Props> = ({
  wells,
  selectedUwi,
  onSelect,
  onManageWells,
  manageWellsHint,
  colorFor,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => filterWells(wells, search), [wells, search]);
  const groups = useMemo(() => groupWells(filtered), [filtered]);

  // Groups start open, as the mock has them, so the wells are visible on load rather
  // than behind a collapsed state and county. Whether a search is running still matters:
  // it remounts the tree (see the `key` below) so a group the user collapsed reopens for
  // the results, and a hit inside it cannot read as "no results".
  const searching = search.trim() !== '';

  // The groups the reader has closed. Under the controllable flag Carbon's TreeNode is
  // fully controlled, so without this every group would be pinned open. A search forces
  // every group open so the matches show without expanding anything.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const isOpen = (id: string) => searching || !collapsed.has(id);
  const toggleGroup = (id: string) => (isExpanded?: boolean) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (isExpanded) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className={styles.headingRow}>
        <h2 className={styles.heading}>
          Wells(
          <FormattedCount value={wells.length} />)
        </h2>
      </div>
      {/* Editing the working set is a draft-only action, so a promoted workspace does not
          offer it at all rather than offering it and refusing. Shape, wording and placement
          match the Inventory Workspace's own add/remove control (type-curve-tree.tsx): the
          two do the same job, and the sheet this opens also DELETES wells — which the words
          say and a bare "+" did not. */}
      {(onManageWells || manageWellsHint) && (
        <div className={styles.actionsRow}>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ListChecked}
            // A hint only ever explains why the control is unavailable. It cannot replace
            // the label, so it goes on `title` rather than into the accessible name — the
            // heading above already supplies the noun, as it does on the inventory rail.
            title={manageWellsHint}
            disabled={!onManageWells}
            onClick={onManageWells}
          >
            Add or remove
          </Button>
        </div>
      )}
      <Search
        size="sm"
        labelText="Search wells"
        placeholder="Search wells, state, county…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
      />

      {groups.length === 0 ? (
        <p className={styles.empty}>No wells match.</p>
      ) : (
        // Without this flag Carbon's TreeView reads `selected` into `useState` once and
        // then owns it, so a card's prev/next caret moved every card but left the tree
        // behind. The flag makes `selected` authoritative. Scoped here rather than set
        // globally, since four other pages render a TreeView uncontrolled.
        <FeatureFlags enableTreeviewControllable>
          <TreeView
            // Carbon's TreeNode reads `isExpanded` once and then owns its open state,
            // so re-rendering will not reopen a mounted node. Keying on search mode
            // remounts twice per search, not per keystroke, so the force-open sticks.
            key={searching ? 'searching' : 'browsing'}
            className={styles.tree}
            label="Workspace wells"
            hideLabel
            size="xs"
            selected={selectedUwi ? [selectedUwi] : []}
            active={selectedUwi ?? undefined}
          >
            {groups.map((stateGroup) => (
              <TreeNode
                key={stateGroup.state}
                id={`state-${stateGroup.state}`}
                // Carbon needs a plain value to match against; the label carries the markup.
                value={stateGroup.state}
                isExpanded={isOpen(`state-${stateGroup.state}`)}
                onToggle={toggleGroup(`state-${stateGroup.state}`)}
                label={
                  <span className={styles.groupLabel}>
                    {stateGroup.state}
                    <span className={styles.groupCount}>
                      <FormattedCount value={stateGroup.count} />
                    </span>
                  </span>
                }
              >
                {stateGroup.counties.map((countyGroup) => (
                  <TreeNode
                    key={countyGroup.county}
                    id={`county-${stateGroup.state}-${countyGroup.county}`}
                    value={countyGroup.county}
                    isExpanded={isOpen(
                      `county-${stateGroup.state}-${countyGroup.county}`
                    )}
                    onToggle={toggleGroup(
                      `county-${stateGroup.state}-${countyGroup.county}`
                    )}
                    label={
                      <span className={styles.groupLabel}>
                        {countyGroup.county}
                        <span className={styles.groupCount}>
                          <FormattedCount value={countyGroup.wells.length} />
                        </span>
                      </span>
                    }
                  >
                    {countyGroup.wells.map((well) => (
                      <TreeNode
                        key={well.uwi}
                        id={well.uwi}
                        value={wellDisplayName(well)}
                        onSelect={() => onSelect(well.uwi)}
                        label={
                          <span className={styles.item}>
                            <span className={styles.itemDot}>
                              <StatusDot
                                status={statusOf(well)}
                                showLabel={false}
                                colorOverride={colorFor?.(well)}
                              />
                            </span>
                            <span className={styles.itemText}>
                              <span className={styles.itemName}>
                                {wellDisplayName(well)}
                              </span>
                              <span className={styles.itemMeta}>
                                UWI {well.uwi} · {statusLabel(well)}
                              </span>
                            </span>
                            {well.editedFields.length > 0 && <EditedPill />}
                          </span>
                        }
                      />
                    ))}
                  </TreeNode>
                ))}
              </TreeNode>
            ))}
          </TreeView>
        </FeatureFlags>
      )}
    </div>
  );
};
